import { isValidSafeAddress, normalizeSafeAddress } from "../../utils/safe-types.mjs";
import { array, boolean, check, integer, lazy, literal, nullable, nullish, number, object, optional, pipe, record, string, transform, tuple, union, unknown, variant } from "valibot";
//#region src/transactions/data/internal.ts
function safeEnum(options) {
	const schemas = Object.keys(options).map((key) => object({
		[key]: options[key],
		$kind: literal(key)
	}));
	return pipe(unknown(), check((input) => unsupportedEnumVariantMessage(input, options) === void 0, ({ input, message }) => unsupportedEnumVariantMessage(input, options) ?? message), transform(addMissingEnumKind), variant("$kind", schemas));
}
function getEnumKind(input) {
	if (typeof input !== "object" || input === null) return null;
	if ("$kind" in input && typeof input.$kind === "string") return input.$kind;
	return Object.keys(input).find((key) => key !== "$kind") ?? null;
}
function addMissingEnumKind(input) {
	if (typeof input !== "object" || input === null || "$kind" in input) return input;
	const kind = getEnumKind(input);
	return kind === null ? input : {
		...input,
		$kind: kind
	};
}
function unsupportedEnumVariantMessage(input, options) {
	const kind = getEnumKind(input);
	if (kind === null || Object.hasOwn(options, kind)) return void 0;
	return `Unsupported enum variant "${kind}". Expected one of: ${Object.keys(options).map((key) => `"${key}"`).join(", ")}.`;
}
function withKind(key, schema) {
	return pipe(object({
		...schema.entries,
		$kind: optional(literal(key))
	}), transform((value) => ({
		...value,
		$kind: key
	})));
}
const SafeAddress = pipe(string(), transform((value) => normalizeSafeAddress(value)), check(isValidSafeAddress));
const ObjectID = SafeAddress;
const BCSBytes = string();
const JsonU64 = pipe(union([string(), pipe(number(), integer())]), check((val) => {
	try {
		BigInt(val);
		return BigInt(val) >= 0 && BigInt(val) <= 18446744073709551615n;
	} catch {
		return false;
	}
}, "Invalid u64"));
const U32 = pipe(number(), integer(), check((val) => val >= 0 && val < 2 ** 32, "Invalid u32"));
const ObjectRefSchema = object({
	objectId: SafeAddress,
	version: JsonU64,
	digest: string()
});
const ArgumentSchema = union([
	withKind("GasCoin", object({ GasCoin: literal(true) })),
	withKind("Input", object({
		Input: pipe(number(), integer()),
		type: optional(union([
			literal("pure"),
			literal("object"),
			literal("withdrawal")
		]))
	})),
	withKind("Result", object({ Result: pipe(number(), integer()) })),
	withKind("NestedResult", object({ NestedResult: tuple([pipe(number(), integer()), pipe(number(), integer())]) }))
]);
const GasDataSchema = object({
	budget: nullable(JsonU64),
	price: nullable(JsonU64),
	owner: nullable(SafeAddress),
	payment: nullable(array(ObjectRefSchema))
});
object({
	address: string(),
	module: string(),
	name: string(),
	typeParams: array(string())
});
const OpenSignatureBodySchema = union([
	object({ $kind: literal("address") }),
	object({ $kind: literal("bool") }),
	object({ $kind: literal("u8") }),
	object({ $kind: literal("u16") }),
	object({ $kind: literal("u32") }),
	object({ $kind: literal("u64") }),
	object({ $kind: literal("u128") }),
	object({ $kind: literal("u256") }),
	object({ $kind: literal("unknown") }),
	object({
		$kind: literal("vector"),
		vector: lazy(() => OpenSignatureBodySchema)
	}),
	object({
		$kind: literal("datatype"),
		datatype: object({
			typeName: string(),
			typeParameters: array(lazy(() => OpenSignatureBodySchema))
		})
	}),
	object({
		$kind: literal("typeParameter"),
		index: pipe(number(), integer())
	})
]);
const OpenSignatureSchema = object({
	reference: nullable(union([
		literal("mutable"),
		literal("immutable"),
		literal("unknown")
	])),
	body: OpenSignatureBodySchema
});
const ProgrammableMoveCallSchema = object({
	package: ObjectID,
	module: string(),
	function: string(),
	typeArguments: array(string()),
	arguments: array(ArgumentSchema),
	_argumentTypes: optional(nullable(array(OpenSignatureSchema)))
});
const $Intent = object({
	name: string(),
	inputs: record(string(), union([ArgumentSchema, array(ArgumentSchema)])),
	data: record(string(), unknown())
});
const CommandSchema = safeEnum({
	MoveCall: ProgrammableMoveCallSchema,
	TransferObjects: object({
		objects: array(ArgumentSchema),
		address: ArgumentSchema
	}),
	SplitCoins: object({
		coin: ArgumentSchema,
		amounts: array(ArgumentSchema)
	}),
	MergeCoins: object({
		destination: ArgumentSchema,
		sources: array(ArgumentSchema)
	}),
	Publish: object({
		modules: array(BCSBytes),
		dependencies: array(ObjectID)
	}),
	MakeMoveVec: object({
		type: nullable(string()),
		elements: array(ArgumentSchema)
	}),
	Upgrade: object({
		modules: array(BCSBytes),
		dependencies: array(ObjectID),
		package: ObjectID,
		ticket: ArgumentSchema
	}),
	$Intent
});
const ObjectArgSchema = safeEnum({
	ImmOrOwnedObject: ObjectRefSchema,
	SharedObject: object({
		objectId: ObjectID,
		initialSharedVersion: JsonU64,
		mutable: boolean()
	}),
	Receiving: ObjectRefSchema
});
const ReservationSchema = safeEnum({ MaxAmountU64: JsonU64 });
const WithdrawalTypeArgSchema = safeEnum({ Balance: string() });
const WithdrawFromSchema = safeEnum({
	Sender: literal(true),
	Sponsor: literal(true),
	SenderAllowance: object({
		funder: SafeAddress,
		allowance: ObjectID
	})
});
const FundsWithdrawalArgSchema = object({
	reservation: ReservationSchema,
	typeArg: WithdrawalTypeArgSchema,
	withdrawFrom: WithdrawFromSchema
});
const CallArgSchema = safeEnum({
	Object: ObjectArgSchema,
	Pure: object({ bytes: BCSBytes }),
	UnresolvedPure: object({ value: unknown() }),
	UnresolvedObject: object({
		objectId: ObjectID,
		version: optional(nullable(JsonU64)),
		digest: optional(nullable(string())),
		initialSharedVersion: optional(nullable(JsonU64)),
		mutable: optional(nullable(boolean()))
	}),
	FundsWithdrawal: FundsWithdrawalArgSchema
});
const NormalizedCallArg = safeEnum({
	Object: ObjectArgSchema,
	Pure: object({ bytes: BCSBytes })
});
const ValidDuringSchema = object({
	minEpoch: nullable(JsonU64),
	maxEpoch: nullable(JsonU64),
	minTimestamp: nullable(JsonU64),
	maxTimestamp: nullable(JsonU64),
	chain: string(),
	nonce: U32
});
const AllowedProposersSchema = object({
	epoch: JsonU64,
	proposers: pipe(array(U32), check((proposers) => proposers.length > 0, "Allowed proposers must not be empty"))
});
const ValiditySchema = object({
	...ValidDuringSchema.entries,
	allowedProposers: nullable(AllowedProposersSchema)
});
const TransactionExpiration = safeEnum({
	None: literal(true),
	Epoch: JsonU64,
	ValidDuring: ValidDuringSchema,
	Validity: ValiditySchema
});
const TransactionDataSchema = object({
	version: literal(2),
	sender: nullish(SafeAddress),
	expiration: nullish(TransactionExpiration),
	gasData: GasDataSchema,
	inputs: array(CallArgSchema),
	commands: array(CommandSchema)
});
//#endregion
export { $Intent, AllowedProposersSchema, ArgumentSchema, BCSBytes, CommandSchema, FundsWithdrawalArgSchema, GasDataSchema, JsonU64, NormalizedCallArg, ObjectArgSchema, ObjectID, ObjectRefSchema, OpenSignatureBodySchema, OpenSignatureSchema, ReservationSchema, SafeAddress, TransactionDataSchema, TransactionExpiration, U32, ValidDuringSchema, ValiditySchema, WithdrawFromSchema, WithdrawalTypeArgSchema, safeEnum };

//# sourceMappingURL=internal.mjs.map